import { clsx } from 'clsx'
import { StatusBadge, type ComplianceStatus } from './StatusBadge'

export type CoverageLine = {
  type: string
  found: string
  required: string
  status: ComplianceStatus
  reason?: string
  /** Optional COI metadata — when present, renders a carrier/policy/expiry
      row for a denser, more screenshot-realistic card (Stage 9.5). */
  carrier?: string
  policy?: string
  expires?: string
}

/**
 * Deterministic-evaluation mock used in hero / Bento proof graphics. Renders a
 * vendor's COI coverage lines with the PASS/FAIL reasoning visible — never real
 * customer data, this is an illustrative product mock.
 */
export function ComplianceCard({
  vendor,
  status = 'compliant',
  lines,
  className,
}: {
  vendor: string
  status?: ComplianceStatus
  lines: CoverageLine[]
  className?: string
}) {
  return (
    <div
      className={clsx(
        className,
        'overflow-hidden rounded-xl bg-white shadow-md ring-1 ring-black/5',
      )}
    >
      <div className="flex items-center justify-between gap-4 border-b border-gray-100 px-5 py-4">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-gray-950">
            {vendor}
          </p>
          <p className="text-xs text-gray-500">ACORD 25 · Evaluated</p>
        </div>
        <StatusBadge status={status} />
      </div>
      <ul className="divide-y divide-gray-100">
        {lines.map((line) => (
          <li key={line.type} className="px-5 py-3">
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm font-medium text-gray-900">
                {line.type}
              </span>
              <StatusBadge
                status={line.status}
                label={line.status === 'compliant' ? 'PASS' : 'FAIL'}
              />
            </div>
            <div className="mt-1 flex flex-wrap gap-x-4 text-xs text-gray-500">
              <span className="tnum">Found: {line.found}</span>
              <span className="tnum">Required: {line.required}</span>
            </div>
            {(line.carrier || line.policy || line.expires) && (
              <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-400">
                {line.carrier && <span>{line.carrier}</span>}
                {line.policy && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span className="tnum">Policy {line.policy}</span>
                  </>
                )}
                {line.expires && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span className="tnum">Exp {line.expires}</span>
                  </>
                )}
              </div>
            )}
            {line.reason && (
              <p className="mt-1 text-xs text-gray-500">{line.reason}</p>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
